import { ITile } from '../types/types';

// Bitmask bit positions (clockwise from top)
const TOP = 1;
const TOPRIGHT = 2;
const RIGHT = 4;
const BOTTOMRIGHT = 8;
const BOTTOM = 16;
const BOTTOMLEFT = 32;
const LEFT = 64;
const TOPLEFT = 128;

// 47 reduced bitmask values (diagonals cleared when adjacent cardinals absent)
const REDUCED_BITMASKS = [
  16, 20, 84, 80, 213, 92, 116, 87, 28, 125, 124, 112, 17, 21, 85, 81, 29, 127,
  253, 113, 31, 119, 245, 1, 5, 69, 65, 23, 223, 247, 209, 95, 255, 221, 241, 0,
  4, 68, 64, 117, 71, 197, 93, 7, 199, 215, 193,
];

// Map reduced bitmask → pattern ID (1-based, matches spritesheet legendId)
const reducedToPatternId = new Map<number, number>();
REDUCED_BITMASKS.forEach((val, i) => reducedToPatternId.set(val, i + 1));

function reduceBitmask(raw: number): number {
  let r = raw;
  if (!(r & TOP) || !(r & RIGHT)) {
    r &= ~TOPRIGHT;
  }
  if (!(r & RIGHT) || !(r & BOTTOM)) {
    r &= ~BOTTOMRIGHT;
  }
  if (!(r & BOTTOM) || !(r & LEFT)) {
    r &= ~BOTTOMLEFT;
  }
  if (!(r & LEFT) || !(r & TOP)) {
    r &= ~TOPLEFT;
  }
  return r;
}

// Pre-computed O(1) lookup table: raw 8-bit bitmask → pattern ID
const BITMASK_TABLE: number[] = [];
for (let raw = 0; raw < 256; raw++) {
  BITMASK_TABLE[raw] = reducedToPatternId.get(reduceBitmask(raw)) ?? 1;
}

export const computeAllTileIndices = (
  tiles: ITile[],
  mapWidth: number,
  mapHeight: number,
): Map<string, number> => {
  const lookup = new Map<string, ITile>();
  for (const tile of tiles) {
    lookup.set(`${tile.x},${tile.y}`, tile);
  }

  const indices = new Map<string, number>();

  for (const tile of tiles) {
    const { x, y, type } = tile;

    const check = (dx: number, dy: number): boolean => {
      const nx = x + dx;
      const ny = y + dy;
      if (nx < 0 || nx >= mapWidth || ny < 0 || ny >= mapHeight) {
        return true;
      }
      const neighbor = lookup.get(`${nx},${ny}`);
      if (!neighbor) {
        return false;
      }
      if (neighbor.type === 'water' && type === 'ground') {
        return true;
      }
      return neighbor.type === type;
    };

    const bitmask =
      (check(0, 1) ? TOP : 0) |
      (check(1, 1) ? TOPRIGHT : 0) |
      (check(1, 0) ? RIGHT : 0) |
      (check(1, -1) ? BOTTOMRIGHT : 0) |
      (check(0, -1) ? BOTTOM : 0) |
      (check(-1, -1) ? BOTTOMLEFT : 0) |
      (check(-1, 0) ? LEFT : 0) |
      (check(-1, 1) ? TOPLEFT : 0);

    indices.set(`${x},${y}`, BITMASK_TABLE[bitmask]);
  }

  return indices;
};
